import { useLazyQuery } from "@apollo/client";
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  MenuItem,
  Alert,
  Paper,
  FormGroup,
  Divider,
} from "@mui/material";
import { FormikProvider } from "formik";
import { times, upperFirst } from "lodash";
import { enqueueSnackbar } from "notistack";
import { ReactElement, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import {
  ComplianceBundle,
  GetRegistrationForStaffQuery,
  TenDlcCampaignRegistrationExpediteStatus,
  SubmitCampaignToTcrMutation,
  SubmitCampaignToTcrMutationVariables,
  UpdateCampaignInTcrMutation,
  UpdateCampaignInTcrMutationVariables,
  ProviderName,
  Copy10DlcRegistrationMutation,
  Copy10DlcRegistrationMutationVariables,
  CheckComplianceBundleSlugQuery,
  CheckComplianceBundleSlugQueryVariables,
  Copy10DlcRegistrationToTollFreeMutation,
  Copy10DlcRegistrationToTollFreeMutationVariables,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { ButtonWithMenu } from "~frontend/components/ButtonWithMenu";
import { FlexBox } from "~frontend/components/FlexBox";
import { TwoColumnDisplay } from "~frontend/components/TwoColumnDisplay";
import {
  FormikDatePicker,
  FormikErrorBanner,
  SubmitButton,
} from "~frontend/components/forms";
import { FormikSelect } from "~frontend/components/forms/FormSelect";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import { CHECK_COMPLIANCE_BUNDLE_SLUG_AVAILABILITY } from "~frontend/graphql/complianceBundles";
import {
  COPY_TENDLC_REGISTRATION,
  COPY_TENDLC_REGISTRATION_TO_TOLL_FREE,
  GET_REGISTRATION_FOR_STAFF,
  SUBMIT_CAMPAIGN_TO_TCR,
  UPDATE_CAMPAIGN_IN_TCR,
} from "~frontend/graphql/tenDlcRegistrations";
import { useStaffEmails } from "~frontend/hooks/useStaffEmails";
import { COMPLIANCE_REGISTRATION_USAGE_TYPE_OPTIONS } from "~frontend/routes/subaccounts/phoneNumberList/TollFreeRegistration";
import { palette } from "~frontend/theme";
import useMutation from "~frontend/util/useMutation";

import { BundleDialog } from "../../compliance-bundles/BundleDialog";
import { COMPLIANCE_STAFF_EMAILS } from "../constants";
import { EventLog } from "./EventLog";
import { ReuseBundleDialog } from "./ReuseBundleDialog";
import {
  initialCampaignValues,
  InputRow,
  useTenDlcContext,
} from "./TenDlcProvider";

export const CampaignDetails = ({
  campaign,
}: {
  campaign: GetRegistrationForStaffQuery["getRegistrationForStaff"]["campaignRegistrations"][0];
}): ReactElement => {
  return (
    <Paper elevation={2} sx={{ px: 2 }}>
      <TwoColumnDisplay
        labelWidth="200px"
        showBottomBorder={false}
        showTopBorder={false}
        rows={[
          {
            key: "status",
            label: "Status",
            content: upperFirst(
              campaign.status?.toLowerCase() ?? "Unknown"
            ).replaceAll("_", " "),
          },
          {
            key: "tcrCampaignId",
            label: "TCR Campaign ID",
            content: campaign.tcrCampaignId ?? "--",
          },
          {
            key: "tcrIdentityStatus",
            label: "TCR Campaign Status",
            content: campaign.tcrStatus ?? "--",
          },
        ]}
      />
      <Divider />
      <Box sx={{ pb: 2, pt: 1 }}>
        <EventLog events={campaign.events} title="Campaign Event Log" />
      </Box>
    </Paper>
  );
};

export const CampaignForm = ({
  campaign,
}: {
  campaign: GetRegistrationForStaffQuery["getRegistrationForStaff"]["campaignRegistrations"][0];
}): ReactElement => {
  const { brand, campaignFormiks } = useTenDlcContext()!;
  const {
    values,
    setFieldValue,
    resetForm,
    isSubmitting,
    isValid,
    dirty,
    submitForm,
    setFieldTouched,
    validateForm,
    setErrors,
    touched,
    errors,
  } = campaignFormiks[campaign.id]!;

  const validateBeforeTcr = async (): Promise<{ hasErrors: boolean }> => {
    await setFieldValue("validateRequiredFields", true);

    const formikErrors = await validateForm();
    if (Object.keys(formikErrors).length > 0) {
      setErrors(formikErrors);
      Object.keys(formikErrors).forEach(
        async field => await setFieldTouched(field, true)
      );
      return { hasErrors: true };
    }

    return { hasErrors: false };
  };

  const [submitToTcr, { loading: isSubmittingToTcr }] = useMutation<
    SubmitCampaignToTcrMutation,
    SubmitCampaignToTcrMutationVariables
  >(SUBMIT_CAMPAIGN_TO_TCR, {
    onErrorSnackbarMessage: "Failed to submit campaign to TCR",
    refetchQueries: [GET_REGISTRATION_FOR_STAFF],
    awaitRefetchQueries: true,
    variables: {
      input: {
        campaignRegistrationId: campaign.id,
      },
    },
  });

  const [copyRegistration] = useMutation<
    Copy10DlcRegistrationMutation,
    Copy10DlcRegistrationMutationVariables
  >(COPY_TENDLC_REGISTRATION, {
    onErrorSnackbarMessage: "Error copying registration",
    variables: {
      input: {
        campaignRegistrationId: campaign.id,
        subaccountId: campaign.subaccountId,
      },
    },
  });

  const [copyRegistrationToTollFree] = useMutation<
    Copy10DlcRegistrationToTollFreeMutation,
    Copy10DlcRegistrationToTollFreeMutationVariables
  >(COPY_TENDLC_REGISTRATION_TO_TOLL_FREE, {
    onErrorSnackbarMessage: "Error copying registration",
    variables: {
      input: {
        campaignRegistrationId: campaign.id,
        subaccountId: campaign.subaccountId,
      },
    },
  });

  const handleSubmitToTcr = async (): Promise<void> => {
    const { hasErrors } = await validateBeforeTcr();
    if (hasErrors) {
      enqueueSnackbar("Please fix the errors before submitting to TCR", {
        variant: "error",
      });
      return;
    }

    resetForm();
    const { data, errors } = await submitToTcr();
    if (errors) {
      enqueueSnackbar(
        <FlexBox gap={1} direction="column">
          <Typography variant="h2Bold" component="div">
            Failed to submit campaign to TCR:
          </Typography>
          {errors.map(error => (
            <Typography key={error.message} variant="body1">
              {error.message}
            </Typography>
          ))}
        </FlexBox>,
        {
          variant: "error",
        }
      );
    } else if (data?.submitCampaignToTcr) {
      resetForm({ values: initialCampaignValues(data.submitCampaignToTcr) });

      enqueueSnackbar("Campaign submitted to TCR successfully", {
        variant: "success",
      });
    } else {
      enqueueSnackbar(
        "Unexpected issue encountered, please reach out to engineering",
        {
          variant: "warning",
        }
      );
    }
  };

  const [updateInTcr, { loading: isUpdatingInTcr }] = useMutation<
    UpdateCampaignInTcrMutation,
    UpdateCampaignInTcrMutationVariables
  >(UPDATE_CAMPAIGN_IN_TCR, {
    onErrorSnackbarMessage: "Failed to update campaign in TCR",
    refetchQueries: [GET_REGISTRATION_FOR_STAFF],
    awaitRefetchQueries: true,
    variables: {
      input: {
        campaignRegistrationId: campaign.id,
      },
    },
  });

  const handleUpdateInTcr = async (): Promise<void> => {
    const { hasErrors } = await validateBeforeTcr();
    if (hasErrors) {
      enqueueSnackbar("Please fix the errors before updating in TCR", {
        variant: "error",
      });
      return;
    }

    resetForm();
    const { data, errors } = await updateInTcr();
    if (errors) {
      enqueueSnackbar(
        <FlexBox gap={1} direction="column">
          <Typography variant="h2Bold" component="div">
            Failed to update campaign in TCR:
          </Typography>
          {errors.map(error => (
            <Typography key={error.message} variant="body1">
              {error.message}
            </Typography>
          ))}
        </FlexBox>,
        {
          variant: "error",
        }
      );
    } else if (data?.updateCampaignInTcr) {
      resetForm({ values: initialCampaignValues(data.updateCampaignInTcr) });
      if (data.updateCampaignInTcr.isDirty) {
        enqueueSnackbar("Campaign updated in TCR successfully", {
          variant: "success",
        });
      }
    } else {
      enqueueSnackbar(
        "Unexpected issue encountered, please reach out to engineering",
        {
          variant: "warning",
        }
      );
    }
  };

  const navigate = useNavigate();

  const handleCopyRegistration = async () => {
    const formikErrors = await validateForm();
    if (Object.keys(formikErrors).length > 0) {
      setErrors(formikErrors);
      Object.keys(formikErrors).forEach(
        async field => await setFieldTouched(field, true)
      );
      enqueueSnackbar("Please fix the errors before copying the campaign", {
        variant: "error",
      });
      return;
    }
    const { errors } = await copyRegistration({
      variables: {
        input: {
          subaccountId: campaign.subaccount.id,
          campaignRegistrationId: campaign.id!,
        },
      },
    });
    if (!errors) {
      enqueueSnackbar("Data copied to new registration", {
        variant: "success",
      });
      await navigate(0);
    }
  };

  const handleCopyRegistrationToTollFree = async () => {
    const formikErrors = await validateForm();
    if (Object.keys(formikErrors).length > 0) {
      setErrors(formikErrors);
      Object.keys(formikErrors).forEach(
        async field => await setFieldTouched(field, true)
      );
      enqueueSnackbar("Please fix the errors before copying the campaign", {
        variant: "error",
      });
      return;
    }
    const { data, errors } = await copyRegistrationToTollFree({
      variables: {
        input: {
          subaccountId: campaign.subaccount.id,
          campaignRegistrationId: campaign.id!,
        },
      },
    });
    if (!errors) {
      enqueueSnackbar("Data copied to new registration", {
        variant: "success",
      });
      const tollFreeRegistrationId = data?.copyTenDlcRegistrationToTollFree.id;
      await navigate(
        `/staff/compliance-registrations/toll-free/${tollFreeRegistrationId}`
      );
    }
  };

  const {
    loading,
    error,
    data: staffEmailData,
    prioritizeEmails,
  } = useStaffEmails();

  const isCampaignSubmitted = !!campaign.tcrCampaignId;
  const campaignNeedsResubmission = campaign.isDirty;

  const [editingBundle, setEditingBundle] = useState<ComplianceBundle | null>(
    null
  );
  const [showReuseBundleDialog, setShowReuseBundleDialog] = useState(false);
  // Use the brand's website as the default slug for the compliance bundle.
  // If that slug is already taken, do not pre-fill it in the bundle dialog. This will require them
  // to enter a new one and trigger validation as usual.
  const [getSlugAvailability] = useLazyQuery<
    CheckComplianceBundleSlugQuery,
    CheckComplianceBundleSlugQueryVariables
  >(CHECK_COMPLIANCE_BUNDLE_SLUG_AVAILABILITY);
  const [defaultBundleSlug, setDefaultBundleSlug] = useState<string | null>(
    null
  );

  useEffect(() => {
    const bundleSlug = brand.website
      ? new URL(brand.website.replace("www.", "")).hostname?.split(".")?.[0]
      : null;

    if (bundleSlug) {
      getSlugAvailability({
        variables: { input: { slug: bundleSlug } },
      })
        .then(({ data }) => {
          if (data?.checkComplianceBundleSlug?.available) {
            setDefaultBundleSlug(bundleSlug);
          } else {
            setDefaultBundleSlug(null);
          }
        })
        .catch(() => {
          setDefaultBundleSlug(null);
        });
    }
  }, [brand.website, getSlugAvailability]);

  return (
    <>
      <BundleDialog
        open={!!editingBundle}
        onClose={async () => {
          setEditingBundle(null);
        }}
        bundle={editingBundle}
        tenDlcCampaignRegistrationId={campaign.id}
      />
      <ReuseBundleDialog
        campaignRegistrationId={campaign.id}
        open={showReuseBundleDialog}
        onClose={() => setShowReuseBundleDialog(false)}
      />
      <AsyncWrapper loading={[loading]} errors={[error]} data={staffEmailData}>
        {staffEmailList => (
          <FormikProvider value={campaignFormiks[campaign.id]}>
            <FormikErrorBanner />

            <Box sx={{ py: 2 }}>
              <CampaignDetails campaign={campaign} />
            </Box>

            <FlexBox direction="column" gap={2}>
              <FlexBox justifyContent="flex-end" alignItems="center" gap={2}>
                <ButtonWithMenu
                  primaryButton={
                    <SubmitButton
                      disabled={!dirty}
                      onClick={async () => {
                        await setFieldValue("validateRequiredFields", false);
                        await submitForm();
                      }}
                      submitText="Save changes"
                      loadingText="Saving..."
                      isSubmitting={isSubmitting}
                    />
                  }
                  menuItems={[
                    {
                      key: "copy-permalink",
                      label: "Copy link",
                      onClick: async () => {
                        await navigator.clipboard.writeText(
                          `${window.location.origin}/staff/compliance-registrations/ten-dlc/${brand.id}/${campaign.id}`
                        );
                        enqueueSnackbar("Link copied to clipboard");
                      },
                    },
                    {
                      key: "reset",
                      label: "Reset changes",
                      onClick: () => resetForm(),
                      visible: dirty,
                      disabled: isSubmitting,
                    },
                    {
                      key: "reuse-compliance-bundle",
                      label: "Reuse compliance bundle",
                      disabled: isSubmitting || dirty,
                      onClick: () => {
                        setShowReuseBundleDialog(true);
                      },
                      visible: !campaign.complianceBundle,
                    },
                    {
                      key: "add-compliance-bundle",
                      label: "Setup compliance bundle",
                      disabled: isSubmitting || dirty,
                      onClick: () => {
                        setEditingBundle({
                          orgName: brand.displayName,
                          bundleTenants: [{ tenantId: campaign.tenantId }],
                          emailContact: brand.organizationContactEmail,
                          messageNature: campaign.description,
                          slug: defaultBundleSlug,
                        } as ComplianceBundle);
                      },
                      visible: !campaign.complianceBundle,
                    },
                    {
                      key: "edit-compliance-bundle",
                      label: "Edit compliance bundle",
                      onClick: () => {
                        if (campaign.complianceBundle) {
                          setEditingBundle(
                            campaign.complianceBundle as ComplianceBundle
                          );
                        }
                      },
                      visible: !!campaign.complianceBundle,
                    },
                    {
                      key: "copy-to-new",
                      label: "Copy to new",
                      disabled: dirty || isSubmitting,
                      onClick: async () => {
                        await handleCopyRegistration();
                      },
                    },
                    {
                      key: "copy-to-toll-free",
                      label: "Copy to toll-free registration",
                      disabled: dirty || isSubmitting,
                      onClick: async () => {
                        await handleCopyRegistrationToTollFree();
                      },
                    },
                    isCampaignSubmitted
                      ? {
                          key: "update-in-tcr",
                          label: "Update in TCR",
                          disabled:
                            dirty ||
                            !isValid ||
                            isUpdatingInTcr ||
                            !campaignNeedsResubmission,
                          onClick: async () => handleUpdateInTcr(),
                        }
                      : {
                          key: "submit-to-tcr",
                          label: "Submit to TCR",
                          disabled:
                            !brand.tcrBrandId ||
                            dirty ||
                            !isValid ||
                            isSubmittingToTcr,
                          onClick: async () => handleSubmitToTcr(),
                        },
                  ]}
                />
              </FlexBox>

              {!brand.tcrBrandId && (
                <Alert severity="info">
                  <FormattedMessage
                    defaultMessage="You must submit the brand to TCR before submitting campaigns."
                    description="Alert for when a campaign's brand has not yet been submitted"
                  />
                </Alert>
              )}
              {campaign.tcrCampaignId && campaignNeedsResubmission && (
                <Alert severity="warning">
                  <FormattedMessage
                    defaultMessage="If this campaign has been modified directly via the TCR site, resubmitting may overwrite those modifications."
                    description="warning about editing a TCR campaign in STWT after editing outside of STWT via TCR"
                  />
                </Alert>
              )}
              <FormikDatePicker
                label="Requested Start Date"
                name="usageStartDate"
                fast
                inputLabelProps={{
                  sx: { marginBottom: "-.25rem", marginTop: ".5rem" },
                }}
                fullWidth
                helperTextProps={{
                  sx: { marginLeft: "14px", fontSize: ".75xem" },
                }}
                helperText="Start date must be more than two business days in the future"
              />
              <FlexBox gap={4} flexWrap="wrap" sx={{ width: "100%" }}>
                <FormikSelect
                  label="Assigned To"
                  name="assignedToStaffEmail"
                  onChange={async e => {
                    await setFieldValue("assignedToStaffEmail", e.target.value);
                  }}
                  withNoneOption
                  fast
                  sx={{ flexGrow: 1, minWidth: "30%" }}
                >
                  {prioritizeEmails(
                    staffEmailList,
                    COMPLIANCE_STAFF_EMAILS
                  ).map(staffEmail => (
                    <MenuItem key={staffEmail.email} value={staffEmail.email}>
                      {staffEmail.fullName}
                    </MenuItem>
                  ))}
                </FormikSelect>

                <FormikSelect
                  label="Expedite Status"
                  name="expediteStatus"
                  fast
                  sx={{ flexGrow: 1, minWidth: "30%" }}
                >
                  <MenuItem
                    value={TenDlcCampaignRegistrationExpediteStatus.None}
                  >
                    None
                  </MenuItem>
                  <MenuItem
                    value={TenDlcCampaignRegistrationExpediteStatus.Requested}
                  >
                    Requested
                  </MenuItem>
                  <MenuItem
                    value={TenDlcCampaignRegistrationExpediteStatus.Paid}
                  >
                    Paid
                  </MenuItem>
                </FormikSelect>

                <Box>
                  {brand.tcrUseCases && brand.tcrUseCases?.length > 0 ? (
                    <FormikSelect
                      label="Use case"
                      name="useCase"
                      disabled={isCampaignSubmitted}
                      fast
                      sx={{ flexGrow: 1, minWidth: "30%" }}
                    >
                      {brand.tcrUseCases.map(({ usecase, mnoMetadata }) => (
                        <MenuItem
                          key={usecase}
                          value={usecase}
                          disabled={
                            !mnoMetadata?.some(metadata => !!metadata.qualify)
                          }
                        >
                          {upperFirst(
                            usecase.replaceAll("_", " ").toLowerCase()
                          )}
                        </MenuItem>
                      ))}
                    </FormikSelect>
                  ) : (
                    <Alert severity="warning" sx={{ fontSize: "14px" }}>
                      No eligible use cases are available. Please check the
                      brand registration.
                    </Alert>
                  )}
                </Box>
              </FlexBox>

              <FlexBox direction="row" alignItems="flex-end" gap={2}>
                <Typography variant="h2" component="div" sx={{ pb: 1 }}>
                  Where will the client use these 10DLC numbers?
                </Typography>
                <FormGroup row>
                  {COMPLIANCE_REGISTRATION_USAGE_TYPE_OPTIONS.map(option => (
                    <FormControlLabel
                      key={option.label}
                      control={
                        <Checkbox
                          name={`usageType-${option.value}`}
                          checked={values.usageTypes?.includes(option.value)}
                          onChange={async e => {
                            if (e.target.checked) {
                              await setFieldValue("usageTypes", [
                                ...(values.usageTypes || []),
                                option.value,
                              ]);
                            } else {
                              await setFieldValue(
                                "usageTypes",
                                values.usageTypes?.filter(
                                  type => type !== option.value
                                )
                              );
                            }
                          }}
                        />
                      }
                      label={option.label}
                    />
                  ))}
                </FormGroup>
                <Box>
                  {touched.usageTypes && errors.usageTypes && (
                    <Typography sx={{ color: palette.red }}>
                      {errors.usageTypes as string}
                    </Typography>
                  )}
                </Box>
              </FlexBox>

              <InputRow>
                <FormikSelect
                  label="Telephony Provider"
                  name="providerName"
                  disabled={isCampaignSubmitted}
                  fast
                >
                  <MenuItem value={ProviderName.Bandwidth}>Bandwidth</MenuItem>
                  <MenuItem value={ProviderName.Sinch}>Sinch</MenuItem>
                </FormikSelect>

                <FormikTextField
                  label="Friendly Name"
                  name="friendlyName"
                  value={values.friendlyName}
                  fast
                />
              </InputRow>

              <FormikTextField
                label="Program Description"
                name="description"
                multiline
                value={values.description}
                fast
              />

              <FlexBox gap={2} justifyContent="space-between" flexWrap="wrap">
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled
                      name="acknowledgedCompliance"
                      value={values.acknowledgedCompliance}
                      checked={values.acknowledgedCompliance}
                      onChange={async e => {
                        await setFieldValue(
                          "acknowledgedCompliance",
                          !!e.target.checked
                        );
                      }}
                    />
                  }
                  label={"Acknowledged compliance requirements?"}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      disabled
                      name="createComplianceBundle"
                      value={values.createComplianceBundle}
                      checked={values.createComplianceBundle}
                      onChange={async e => {
                        await setFieldValue(
                          "createComplianceBundle",
                          !!e.target.checked
                        );
                      }}
                    />
                  }
                  label="Requested compliance bundle?"
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      name="embeddedPhone"
                      value={values.embeddedPhone}
                      checked={values.embeddedPhone}
                      onChange={async e => {
                        await setFieldValue(
                          "embeddedPhone",
                          !!e.target.checked
                        );
                      }}
                    />
                  }
                  label="Embedding phone number in texts?"
                />
              </FlexBox>

              <FormikTextField
                fullWidth
                label="Message Flow"
                name="messageFlowStaff"
                fast
              />

              <InputRow>
                <FormikTextField
                  label="Terms and Conditions Link"
                  name="termsAndConditionsLink"
                  fast
                />
                <FormikTextField
                  label="Privacy Policy Link"
                  name="privacyPolicyLink"
                  fast
                />
              </InputRow>

              <InputRow>
                <FormikTextField
                  label="Opt-In Form Link"
                  name="optInLink"
                  fast
                />

                <FormikTextField
                  label="Opt-In Confirmation Message"
                  name="optInConfirmationMessage"
                  fast
                  multiline
                />
              </InputRow>

              <InputRow>
                <FormikTextField
                  label="Help Message"
                  name="helpMessage"
                  multiline
                  fast
                />

                <FormikTextField
                  label="Opt-Out Message"
                  name="optoutMessage"
                  multiline
                  fast
                />
              </InputRow>

              <FlexBox direction="column" gap={2}>
                <Box>
                  <Typography variant="h2Bold" sx={{ fontWeight: 600 }}>
                    Example Scripts
                  </Typography>
                </Box>
                {times(4).map(i => (
                  <FormikTextField
                    label={`Sample Script ${i + 1}${i < 2 ? "" : " (optional)"}`}
                    name={`sampleScript${i + 1}`}
                    key={`sampleScript${i + 1}`}
                    multiline
                    minRows={3}
                    maxRows={10}
                    bottomLabel="Sample scripts must be between 20 and 1024 characters"
                    fast
                  />
                ))}

                <FormikTextField
                  multiline
                  label="Additional context"
                  name="additionalContext"
                  disabled={isCampaignSubmitted}
                  fast
                />
              </FlexBox>

              <FlexBox gap={2} justifyContent="flex-end">
                <Button
                  disabled={isSubmitting || !dirty}
                  onClick={() => resetForm()}
                >
                  Reset changes
                </Button>
                <SubmitButton
                  disabled={!dirty}
                  onClick={async () => {
                    await setFieldValue("validateRequiredFields", false);
                    await submitForm();
                  }}
                  submitText="Save changes"
                  loadingText="Saving..."
                  isSubmitting={isSubmitting}
                />
              </FlexBox>
            </FlexBox>
          </FormikProvider>
        )}
      </AsyncWrapper>
    </>
  );
};
